"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RefundTransport = exports.RefundLineItemTransport = void 0;
const typebox_1 = require("@sinclair/typebox");
const common_1 = require("../../common");
const payment_processor_1 = require("./payment-processor");
const payment_status_1 = require("./payment-status");
/**
 * A line on the refund. Additive: a refund adds its own lines and never edits the invoice's.
 * Cents only.
 */
exports.RefundLineItemTransport = typebox_1.Type.Object({
    itemName: typebox_1.Type.String(),
    description: typebox_1.Type.String(),
    quantity: typebox_1.Type.Number(),
    /** Rate in cents. Fractional for per-ton and percent-based lines, mirroring the invoice line. */
    unitPrice: typebox_1.Type.Number(),
    totalPrice: typebox_1.Type.Integer(),
    taxable: typebox_1.Type.Boolean(),
    tax: typebox_1.Type.Integer(),
    /** The invoice line this cancels. Pointer to original invoice line item. */
    originalLineItemID: typebox_1.Type.String({ format: 'uuid' }),
});
/**
 * Unified billing (v2) refund. Structurally the v1 refund minus the `*Dollars` mirrors, with
 * system-agnostic reference ids, and with the payment it reverses named outright instead of
 * implied by a processor charge id — optionally, so one refund can cover an invoice paid by
 * several payments.
 */
exports.RefundTransport = typebox_1.Type.Object({
    id: typebox_1.Type.String({ format: 'uuid' }),
    invoiceID: typebox_1.Type.String({ format: 'uuid' }),
    haulerID: typebox_1.Type.String({ format: 'uuid' }),
    date: typebox_1.Type.String({ format: 'date-time' }),
    /** Cents. */
    amount: typebox_1.Type.Integer(),
    paymentMethod: common_1.PaymentMethod,
    refundedByUserID: typebox_1.Type.String(),
    /**
     * Check number, wire reference — identifies an off-platform refund. Card refunds leave it unset. A
     * refund spanning several payments carries the single identifier of the money actually returned, not
     * one per payment reversed.
     */
    refundIdentifier: typebox_1.Type.Optional(typebox_1.Type.String()),
    reason: typebox_1.Type.Optional(typebox_1.Type.String()),
    notes: typebox_1.Type.Optional(typebox_1.Type.String()),
    /**
     * What handled the refund. `MANUAL` whenever the money went back off-platform (check, cash) or as
     * account credit — independent of how the original payment was taken, so a card payment reversed to
     * account credit is `MANUAL` here.
     */
    paymentProcessor: payment_processor_1.PaymentProcessor,
    /**
     * That system's own id for this refund. Absent when a Rainforest payin was voided rather than
     * refunded, and always absent when `paymentProcessor` is `MANUAL`. Opaque, not a foreign key.
     */
    paymentProcessorReferenceID: typebox_1.Type.Optional(typebox_1.Type.String()),
    /**
     * Lifecycle status of the money movement itself. `MANUAL` refunds are recorded only once the
     * money is already known to have moved, so they default to `COMPLETED`; a processor refund may
     * sit at `PENDING`, `AUTHORIZING`, or `PROCESSING` until it clears. Distinct from `type` below,
     * which classifies the refund as a void vs. a true refund rather than tracking whether the
     * money has actually moved yet.
     */
    status: payment_status_1.PaymentStatus,
    /**
     * The payment this refund reverses, when it reverses exactly one — `amount` then never exceeds that
     * payment's amount. Points at a `PaymentTransport.id` on this invoice, so it holds for check, cash,
     * and account-credit refunds where no processor reference exists on either side; the processor id the
     * reversal call needs is read through this payment rather than copied.
     *
     * Unset means the refund applies to the invoice as a whole: it may reverse several payments at once
     * (one check returning money taken as a card payment plus an ACH payment), or reverse none at all — a
     * write-off where `amount` is 0 and `lineItems` cancels the charges. Which payments a spanning refund
     * covered, and how much came off each, is deliberately not recorded: the balance is an invoice-level
     * figure and the processor enforces its own per-charge refund cap at call time. The invariant is
     * `sum(refunds.amount) <= sum(payments.amount)` across the invoice.
     */
    originalPaymentID: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    /**
     * Voiding reverses an uncaptured payment — the money never actually moved; refunding returns
     * money that was captured. `status` tracks the lifecycle of that movement (or lack of it); `type`
     * says which kind of reversal this is, a distinction `status` alone cannot express. Meaningful
     * only alongside `originalPaymentID` — a void names the one payment it reverses, so it never
     * spans.
     */
    type: typebox_1.Type.Optional(typebox_1.Type.Union([typebox_1.Type.Literal('VOID'), typebox_1.Type.Literal('REFUND')])),
    /**
     * Charges this refund cancels. Empty means only the payment was reversed, so the customer owes the
     * invoice again. Listing charges also writes them off, so the invoice settles at zero instead.
     * `amount` is the money returned; these lines are the charges cancelled — they need not match.
     */
    lineItems: typebox_1.Type.Array(exports.RefundLineItemTransport),
    metadata: typebox_1.Type.Optional(common_1.MetaData),
});
//# sourceMappingURL=refund-transport.js.map